/**
 * BookResourcePage — authenticated booking flow for one resource.
 *
 * User picks from_ts/to_ts → server-side availability check (api-bookings) →
 * server-side create. Payment integration intentionally deferred: status
 * lands as 'reserved' and a separate payment flow (Stripe webhook) advances
 * to 'paid' which then issues the key. For prototyping with free resources,
 * caller can fast-path via the /webhooks/payment endpoint or paid pricing
 * with pricing_type='free' which yields amount=0.
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CalendarDays, ArrowLeft } from 'lucide-react';
import { Header } from '@/components/layout/Header';
import { bookingApi, type BookingResource, type PriceQuote } from '@/lib/bookingApi';

export default function BookResourcePage() {
  const { resourceId } = useParams<{ resourceId: string }>();
  const navigate = useNavigate();

  const [resource, setResource] = useState<BookingResource | null>(null);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [quote, setQuote] = useState<PriceQuote | null>(null);
  const [conflict, setConflict] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!resourceId) return;
    bookingApi.listResources().then((r) => {
      setResource(r.resources.find((x) => x.id === resourceId) ?? null);
    }).catch(() => {});
  }, [resourceId]);

  const checkAvailability = async () => {
    if (!resourceId || !from || !to) return;
    setLoading(true);
    setError(null);
    setConflict(false);
    try {
      const fromIso = new Date(from).toISOString();
      const toIso = new Date(to).toISOString();
      const res = await bookingApi.availability(resourceId, fromIso, toIso);
      setQuote(res.price ?? null);
      const overlap = res.occupied.some((o) => {
        const oFrom = Date.parse(o.from_ts);
        const oTo = Date.parse(o.to_ts);
        return oFrom < Date.parse(toIso) && oTo > Date.parse(fromIso);
      });
      setConflict(overlap);
    } catch (e: any) {
      setError(e?.message ?? 'Kunde inte kontrollera tillgänglighet');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!resourceId || !from || !to) return;
    setSubmitting(true);
    setError(null);
    try {
      const fromIso = new Date(from).toISOString();
      const toIso = new Date(to).toISOString();
      const res = await bookingApi.createBooking({ resource_id: resourceId, from_ts: fromIso, to_ts: toIso });
      // Naive next step — for free pricing we land in 'reserved' and the
      // user needs an admin / payment flow to advance. We surface the
      // booking_id and direct back to the list.
      alert(`Bokning skapad: ${res.booking_id}. Status: ${res.status}.`);
      navigate('/bookings');
    } catch (e: any) {
      const msg = e?.message ?? 'Kunde inte skapa bokning';
      if (msg.includes('slot_unavailable')) {
        setError('Tiden är upptagen. Välj en annan.');
      } else {
        setError(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Header />
      <main className="px-4 py-6 max-w-2xl mx-auto space-y-4">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-muted-foreground">
          <ArrowLeft className="h-3.5 w-3.5" /> Tillbaka
        </button>

        {!resource ? (
          <p className="text-muted-foreground">Resursen kunde inte hittas.</p>
        ) : (
          <div className="space-y-4">
            <div className="rounded-2xl border border-border bg-card/50 p-4">
              <div className="flex items-center gap-2 mb-2">
                <CalendarDays className="h-4 w-4" />
                <h2 className="text-base font-semibold">{resource.name}</h2>
              </div>
              <p className="text-sm text-muted-foreground">
                Kapacitet {resource.capacity}, tidszon {resource.timezone}
              </p>
            </div>

            <div className="rounded-2xl border border-border bg-card/50 p-4 space-y-3">
              <label className="block">
                <span className="text-sm font-medium">Från</span>
                <input
                  type="datetime-local"
                  className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2"
                  value={from}
                  onChange={(e) => { setFrom(e.target.value); setQuote(null); setConflict(false); }}
                />
              </label>
              <label className="block">
                <span className="text-sm font-medium">Till</span>
                <input
                  type="datetime-local"
                  className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2"
                  value={to}
                  onChange={(e) => { setTo(e.target.value); setQuote(null); setConflict(false); }}
                />
              </label>
              <button
                onClick={checkAvailability}
                disabled={!from || !to || loading}
                className="w-full rounded-lg bg-secondary px-4 py-2 text-sm font-medium hover:bg-secondary/80 disabled:opacity-50"
              >
                {loading ? 'Kontrollerar…' : 'Kontrollera tillgänglighet'}
              </button>
            </div>

            {quote && (
              <div className="rounded-2xl border border-border bg-card/50 p-4">
                <p className="text-sm font-medium mb-1">Pris</p>
                {quote.amount > 0 ? (
                  <p>{quote.amount.toFixed(2)} {quote.currency} ({quote.pricing_type})</p>
                ) : (
                  <p className="text-emerald-600">Gratis</p>
                )}
                {conflict && <p className="mt-2 text-rose-500 text-sm">Tiden är upptagen.</p>}
              </div>
            )}

            {error && <p className="text-rose-500 text-sm">{error}</p>}

            <button
              onClick={handleSubmit}
              disabled={!from || !to || submitting || conflict}
              className="w-full rounded-lg bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {submitting ? 'Skapar bokning…' : 'Bekräfta bokning'}
            </button>
          </div>
        )}
      </main>
    </>
  );
}
